"use client";
import { useState } from "react";
import Image from "next/image";
import CommonSubtitle from "../../common/CommonSubtitle";
import checkIcon from "@/assets/images/prebuilt-ai-tab/tick.svg";
import {
  PREBUILT_TABS_CONFIG,
  CategoryId,
} from "./prebuiltAiTabConfig";
import {
  AgentContentData,
  AgentContent,
  PREBUILT_AGENTS_CONTENT,
} from "./contentData";
import CommonTitle from "@/components/common/CommonTitle";

interface PrebuiltAiTabProps {
  contentData?: AgentContentData;
  defaultActiveId?: CategoryId;
  title?: React.ReactNode;
  subtitle?: string;
}

const PrebuiltAiTab = ({
  contentData,
  defaultActiveId = "project",
  title,
  subtitle,
}: PrebuiltAiTabProps) => {
  const [activeId, setActiveId] = useState<CategoryId>(defaultActiveId);
  const dataToUse = contentData || PREBUILT_AGENTS_CONTENT;
  const activeTab =
    PREBUILT_TABS_CONFIG.find((tab) => tab.id === activeId) ??
    PREBUILT_TABS_CONFIG[0];
  const activeContent: AgentContent | undefined = dataToUse[activeId];

  return (
    <section className="bg-white py-12 xl:py-20">
      <div className="container mx-auto px-4">
        <div className="mb-8 text-center xl:mb-10">
          <CommonTitle
            color="#202020"
            fontSizeClass="text-[24px] lg:text-[32px] mb-2 lg:mb-4 text-center mx-auto max-w-[800px]"
            fontWeightClass="font-semibold"
            as="h2"
          >
            {title || (
              <>
                <span className="bg-gradient-to-r from-[#781E8F] to-[#422CBB] bg-clip-text text-transparent">
                  Prebuilt AI agents perform{" "}
                </span>
                tasks in seconds that take employees hours or longer to
                complete.
              </>
            )}
          </CommonTitle>
          <CommonSubtitle
            color="#202020"
            fontSizeWeight="max-w-[800px] mx-auto text-center"
          >
            {subtitle ||
              "Turn every employee into a rockstar of efficiency with prebuilt AI agents."}
          </CommonSubtitle>
        </div>
        <div className="mx-auto mb-8 flex flex-wrap justify-center gap-2 lg:mb-10">
          {PREBUILT_TABS_CONFIG.map((tab) => {
            const isActive = tab.id === activeId;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveId(tab.id)}
                className={`rounded-lg border px-3 py-1.5 text-sm font-normal lg:px-4 lg:py-2 ${isActive
                  ? "border-[#202020] bg-gradient-to-r from-[#781E8F] to-[#422CBB] bg-clip-text text-transparent"
                  : "border-[#E0E0EA] bg-white text-[#4A4A4A] hover:border-[#C5C5D8]"
                  }`}
              >
                {tab.tabLabel}
              </button>
            );
          })}
        </div>
        <div className="relative overflow-hidden rounded-3xl bg-[#F6F5FF] p-4 sm:p-6 lg:p-8">
          <div className="flex flex-col items-stretch gap-6 xl:flex-row xl:items-center xl:gap-8 xl:justify-between">
            <div className="w-full xl:w-1/2 xl:max-w-[520px]">
              <div className="inline-flex rounded-full bg-white px-3 py-1 text-sm font-semibold">
                <span className="bg-gradient-to-r from-[#781E8F] to-[#422CBB] bg-clip-text text-transparent">
                  {activeTab.badgeLabel}
                </span>
              </div>
              {activeContent && (
                <>
                  <CommonTitle
                    color="#202020"
                    fontSizeClass="text-[20px] xl:text-[22px] mt-4"
                    fontWeightClass="font-semibold"
                    as="h3"
                  >
                    {activeContent.agentName}
                  </CommonTitle>
                  <CommonSubtitle color="#202020" fontSizeWeight="mt-1">
                    {activeContent.description}
                  </CommonSubtitle>
                  <div className="mt-4 rounded-2xl border border-dashed border-[#D5D3F0] bg-white/60 p-3 lg:p-4">
                    <p className="mb-1 text-[16px] font-semibold text-[#202020] sm:text-[18px]">
                      {activeContent.useCase.title}
                    </p>
                    <p className="text-[13px] leading-relaxed text-[#717171] xl:text-[14px]">
                      "{activeContent.useCase.body}"
                    </p>
                  </div>
                  <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-[14px] text-[#202020] sm:gap-x-8 lg:text-[15px]">
                    {activeContent.benefits.map((benefit) => (
                      <div
                        key={benefit.id}
                        className="flex items-center gap-2 text-[15px]"
                      >
                        <Image
                          src={checkIcon}
                          alt={benefit.label}
                          width={18}
                          height={18}
                        />
                        <span>{benefit.label}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="relative hidden h-[250px] w-full overflow-hidden sm:h-[300px] md:h-[350px] lg:h-full lg:w-1/2 xl:block">
              <Image
                src={activeTab.image}
                alt={activeContent?.agentName || activeTab.tabLabel}
                className="h-full w-full object-cover"
                priority
                draggable={false}
                quality={100}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PrebuiltAiTab;
